/**
 * api.js — Central HTTP utility (login + generic saas/* calls)
 *
 * After login, call api.setSession(data) once.
 * Every subsequent request automatically injects:
 *   Authorization: Bearer <accessToken>
 *   X-Emp-Id:      <emp_id>
 *   X-Tenant-Id:   <tenantId>
 *   X-Service-Code <feature code>  (when passed)
 *
 * The session itself is no longer kept only in memory — it is delegated to
 * sessionDB.js so it survives page refreshes and can be read by every other
 * module (src/api, src/lib, src/services) that was merged into this app.
 */
import sessionDB from './sessionDB';
import { isSessionExpiring } from './sessionExpiry';
import { API_BASE_URL, LOGIN_URL } from '../api/config';

async function parseJsonResponse(res) {
  const text = await res.text();
  let json = {};
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      // CRA proxy returns plain text like "Proxy error: Could not proxy..."
      // when the backend is unreachable — surface that clearly.
      const trimmed = text.trim();
      if (/^proxy error/i.test(trimmed)) {
        throw new Error(
          `Cannot reach API server (${API_BASE_URL}). Check that the backend is running, or set REACT_APP_API_BASE_URL.`
        );
      }
      throw new Error(trimmed.slice(0, 200) || `Request failed (${res.status})`);
    }
  }
  return json;
}

let refreshPromise = null;

export function getAccessTokenExpiry(session) {
  const token = session.accessToken || '';
  try {
    const payload = token.split('.')[1];
    if (payload) {
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(base64));
      if (Number.isFinite(decoded.exp)) return decoded.exp * 1000;
    }
  } catch {
    // A non-JWT token can still use the expiry recorded from the login response.
  }
  return Number(session.accessTokenExpiresAt) || 0;
}

const api = {
  setSession(data) { sessionDB.saveSession(data); },
  clearSession()   { sessionDB.clearSession(); },
  getSession()     { return sessionDB.getSession(); },

  _headers(serviceCode = null, extra = {}) {
    const headers = { 'Content-Type': 'application/json', ...extra };
    const session = sessionDB.getSession();
    if (session.accessToken) {
      headers['Authorization']  = `Bearer ${session.accessToken}`;
      const empId = sessionDB.getEmpId();
      const tenantId = sessionDB.getTenantId();
      if (empId)    headers['X-Emp-Id']    = String(empId);
      if (tenantId) headers['X-Tenant-Id'] = String(tenantId);
    }
    if (serviceCode) headers['X-Service-Code'] = serviceCode;
    return headers;
  },

  _url(path) {
    if (/^https?:\/\//i.test(path)) return path;
    return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  },

  async get(url, serviceCode = null) {
    const res = await this._fetchWithRefresh(() =>
      fetch(this._url(url), { method: 'GET', headers: this._headers(serviceCode) })
    );
    const json = await parseJsonResponse(res);
    if (!res.ok) throw new Error(json.message || `GET ${url} failed (${res.status})`);
    return json;
  },

  async post(url, body = {}, serviceCode = null) {
    const res = await this._fetchWithRefresh(() =>
      fetch(this._url(url), { method: 'POST', headers: this._headers(serviceCode), body: JSON.stringify(body) })
    );
    const json = await parseJsonResponse(res);
    if (!res.ok) throw new Error(json.message || `POST ${url} failed (${res.status})`);
    return json;
  },

  async put(url, body = {}, serviceCode = null) {
    const res = await this._fetchWithRefresh(() =>
      fetch(this._url(url), { method: 'PUT', headers: this._headers(serviceCode), body: JSON.stringify(body) })
    );
    const json = await parseJsonResponse(res);
    if (!res.ok) throw new Error(json.message || `PUT ${url} failed (${res.status})`);
    return json;
  },

  async delete(url, serviceCode = null) {
    const res = await this._fetchWithRefresh(() =>
      fetch(this._url(url), { method: 'DELETE', headers: this._headers(serviceCode) })
    );
    const json = await parseJsonResponse(res);
    if (!res.ok) throw new Error(json.message || `DELETE ${url} failed (${res.status})`);
    return json;
  },

  /**
   * Login — uses X-Service-Code: LOGIN_REQ, no Bearer yet.
   * Hits the same gateway as HRMS/ESS modules (not the CRA localhost proxy).
   */
  async login(email, password, tenantId, rememberMe = false) {
    const res = await fetch(LOGIN_URL, {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'X-Service-Code': 'LOGIN_REQ',
        'X-Tenant-Id': String(tenantId),
      },
      body: JSON.stringify({ email, password, tenantId, rememberMe }),
    });
    const json = await parseJsonResponse(res);
    if (!res.ok) throw new Error(json.message || 'Login Failed : Unknown Error');
    return json;
  },

  /** Renew an expired or soon-to-expire access token using the saved refresh token. */
  async refreshAccessToken({ userInitiated = false } = {}) {
    if (refreshPromise) return refreshPromise;

    const session = sessionDB.getSession();
    if ((!userInitiated && session.rememberMe !== true) || !session.accessToken || !session.refreshToken) {
      throw new Error('Your session has expired. Please sign in again.');
    }

    refreshPromise = (async () => {
      const res = await fetch(this._url('/saas/login/refresh'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Service-Code': 'LOGIN_REQ',
          ...(sessionDB.getTenantId() ? { 'X-Tenant-Id': String(sessionDB.getTenantId()) } : {}),
        },
        body: JSON.stringify({ refreshToken: session.refreshToken }),
      });
      const json = await parseJsonResponse(res);
      if (!res.ok) throw new Error(json.message || 'Unable to refresh your session. Please sign in again.');

      const data = json.data && typeof json.data === 'object' ? json.data : json;
      const accessToken = data.accessToken || data.access_token;
      if (!accessToken) throw new Error('The refresh response did not include an access token.');

      if (isSessionExpiring(session.accessToken) || sessionDB.getAccessToken() !== session.accessToken) throw new Error('Session changed during refresh.');
      return sessionDB.updateSession({
        ...data,
        rememberMe: session.rememberMe === true,
        accessToken,
        refreshToken: data.refreshToken || data.refresh_token || session.refreshToken,
        accessTokenExpiresAt: data.expiresIn
          ? Date.now() + Number(data.expiresIn) * 1000
          : session.accessTokenExpiresAt,
      });
    })();

    try {
      return await refreshPromise;
    } finally {
      refreshPromise = null;
    }
  },

  /** Refresh only when the access token is expired or has less than a minute left. */
  async ensureValidAccessToken() {
    const session = sessionDB.getSession();
    if (session.rememberMe !== true || !session.accessToken || !session.refreshToken) return session;

    const expiresAt = getAccessTokenExpiry(session);
    if (expiresAt && expiresAt - Date.now() <= 60 * 1000) {
      return this.refreshAccessToken();
    }
    return session;
  },

  /** Run an authenticated request and retry it once after a 401 for remembered sessions. */
  async _fetchWithRefresh(requestFactory) {
    await this.ensureValidAccessToken();
    const requestToken = sessionDB.getAccessToken();
    let res = await requestFactory();
    const session = sessionDB.getSession();
    if (res.status === 401 && session.rememberMe === true && session.refreshToken) {
      // Another concurrent request may already have refreshed the token while
      // this request was in flight. In that case only retry with the new token.
      if (session.accessToken === requestToken) await this.refreshAccessToken();
      res = await requestFactory();
    }
    return res;
  },

  /** Public tenant registration; ApiResponse<Map<String, Integer>>. */
  async register(body) {
    const res = await fetch(this._url('/saas/login/onboard/tenant'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Service-Code': 'LOGIN_REQ' },
      body: JSON.stringify(body),
    });
    const json = await parseJsonResponse(res);
    if (!res.ok || json.success === false) {
      throw new Error(json.message || 'Unable to submit your registration. Please try again.');
    }
    return json;
  },
  /** Public employee onboarding calls share the login gateway headers. */
  async _employeeOnboardPost(path, body) {
    const res = await fetch(this._url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Service-Code': 'LOGIN_REQ' },
      body: JSON.stringify(body),
    });
    const json = await parseJsonResponse(res);
    if (!res.ok || json.success === false) {
      throw new Error(json.message || 'Unable to submit your onboarding request. Please try again.');
    }
    return json;
  },

  employeeGetCode(body) {
    return this._employeeOnboardPost('/saas/login/onboard/emp/get-code', body);
  },

  async employeeVerifyCode(code, tenantId) {
    const res = await fetch(this._url('/saas/login/onboard/emp/verify-code?tenantId=' + encodeURIComponent(tenantId)), {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain', 'X-Service-Code': 'LOGIN_REQ' },
      body: code,
    });
    const json = await parseJsonResponse(res);
    if (!res.ok || json.success === false) {
      throw new Error(json.message || 'Unable to verify your onboarding code. Please try again.');
    }
    return json;
  },

  employeeRegister(body) {
    return this._employeeOnboardPost('/saas/login/onboard/emp/register', body);
  },

  /** Shared headers for unauthenticated forgot-password calls. */
  _forgotPasswordHeaders(tenantId) {
    return {
      'Content-Type': 'application/json',
      'X-Service-Code': 'LOGIN_REQ',
      'X-Tenant-Id': String(tenantId),
    };
  },

  async _forgotPasswordPost(path, body, tenantId) {
    const res = await fetch(this._url(path), {
      method: 'POST',
      headers: this._forgotPasswordHeaders(tenantId),
      body: JSON.stringify(body),
    });
    const json = await parseJsonResponse(res);
    if (!res.ok) throw new Error(json.message || json.data || `Request failed (${res.status})`);
    return json;
  },

  /** POST /saas/login/fp/getotp */
  async forgotPasswordGetOtp(email, tenantId) {
    return this._forgotPasswordPost(
      '/saas/login/fp/getotp',
      { email, tenantId: Number(tenantId) },
      tenantId
    );
  },

  /** POST /saas/login/fp/validateotp */
  async forgotPasswordValidateOtp(email, tenantId, otp) {
    return this._forgotPasswordPost(
      '/saas/login/fp/validateotp',
      { email, tenantId: Number(tenantId), otp },
      tenantId
    );
  },

  /** POST /saas/login/fp/changepwd */
  async forgotPasswordChangePwd(email, newPwd, confNewPwd, tenantId) {
    return this._forgotPasswordPost(
      '/saas/login/fp/changepwd',
      { email, newPwd, confNewPwd, tenantId: Number(tenantId) },
      tenantId
    );
  },
};

export default api;
